import React, { useEffect, useState } from "react"

import { Check, Copy, Loader2, Workflow } from "lucide-react"
import { toast } from "sonner"

import type { JobPostingStage } from "@/components/applicants/DocumentsList"
import { stagesConfig } from "@/components/job-postings/KanbanStageConfig"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { allReasons } from "@/config/stageDialogConfig"
import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"

import { PermissionGuard } from "../common/PermissionGuard"
import RoleTeamGuard from "../guards/RoleTeamGuard"
import CommentsSection from "./CommentsSection"
import MoveStageDialog from "./MoveStageDialog"

interface StatusPanelProps {
  status: string
  current_stage: string
  segment?: string | null
  stage_reason: string
  stage_note: string
  onEditClick: () => void
  lastEditedBy?: string
  lastCommunication?: {
    date: string
    type: string
  }
  name?: string
  applicationId: string
  id: string
  personAirtableId?: string | null
  primary_person_id?: string | null
  personId?: string
  allowedStages?: JobPostingStage[]
  contract_salary?: number | null
  onContractSalaryChange?: (value: number | null) => void
}

const StatusPanel = ({
  status,
  current_stage: stage,
  segment,
  stage_reason,
  stage_note,
  onEditClick,
  name: applicantName = "Applicant",
  id,
  applicationId,
  personAirtableId: airtableId,
  primary_person_id: primaryPersonId,
  personId,
  allowedStages = [],
  contract_salary,
  onContractSalaryChange,
}: StatusPanelProps) => {
  const { claims } = useAuth()
  const isDSP = claims.user_role === "DSP"
  const [isMoveStageDialogOpen, setIsMoveStageDialogOpen] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [salaryValue, setSalaryValue] = useState<string>(
    contract_salary != null ? String(contract_salary) : "",
  )
  const [salaryDirty, setSalaryDirty] = useState(false)
  const [isSavingSalary, setIsSavingSalary] = useState(false)

  useEffect(() => {
    setSalaryValue(contract_salary != null ? String(contract_salary) : "")
    setSalaryDirty(false)
  }, [contract_salary])

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
        toast.success("Copied to Clipboard", {
          description: "Application ID has been copied to clipboard",
        })
      })
      .catch((err) => {
        toast.error("Failed to Copy", {
          description: "Could not copy to clipboard",
        })
        console.error("Failed to copy: ", err)
      })
  }

  const renderStageReason = (stageReason: string) => {
    const reason = allReasons.find((reason) => reason.value === stageReason)
    return reason?.label
  }

  const renderCurrentStage = (stage: string) => {
    const stageConfig = stagesConfig.find((config) => config.stage === stage)
    if (stageConfig) {
      const Icon = stageConfig.icon
      return (
        <div className="flex items-center gap-2 mt-1">
          <div
            className="p-1.5 rounded-md border-2"
            style={{ backgroundColor: `${stageConfig.color}20` }}
          >
            <Icon className="w-4 h-4" style={{ color: stageConfig.color }} />
          </div>
          <p className="font-semibold">{stageConfig.label}</p>
        </div>
      )
    }
    return <p className="font-semibold text-primary">{stage}</p>
  }

  const getSegmentLabel = (stageSlug: string, seg?: string | null) => {
    if (!seg) return null
    const stageConfig = stagesConfig.find((s) => s.stage === stageSlug || s.id === stageSlug)
    const segmentCfg = stageConfig?.segments?.find((s) => s.id === seg)
    return segmentCfg?.label ?? seg
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h2 className="text-xl font-semibold text-secondary mb-4">Application Details</h2>
      <div className="space-y-4">
        <div>
          <p className="text-sm text-palette-jungleGreen500">Application ID</p>
          <div className="flex items-center gap-2">
            <p className="font-semibold text-primary">{applicationId}</p>
            <button
              onClick={() => copyToClipboard(applicationId)}
              className="p-1 hover:bg-palette-jungleGreen100 rounded-full transition-colors"
              title="Copy to clipboard"
            >
              {isCopied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4 text-palette-jungleGreen500" />
              )}
            </button>
          </div>
        </div>
        <div>
          <p className="text-sm text-palette-jungleGreen500">Airtable Person ID</p>
          <p className="font-semibold text-primary">{airtableId ? airtableId : "Not assigned"}</p>
        </div>
        {primaryPersonId ? (
          <div>
            <p className="text-sm text-palette-jungleGreen500">Primary Person ID</p>
            <p className="font-semibold text-primary">{primaryPersonId}</p>
          </div>
        ) : null}
        <div>
          <p className="text-sm text-palette-jungleGreen500">Current Status</p>
          <p className="font-semibold text-primary">{status}</p>
        </div>
        <RoleTeamGuard roles={["Admin", "Manager"]}>
          <div>
            <p className="text-sm text-palette-jungleGreen500">Contract Salary</p>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                className="flex-1"
                inputMode="numeric"
                step="1"
                value={salaryValue}
                disabled={isSavingSalary}
                onChange={(e) => {
                  setSalaryValue(e.target.value)
                  setSalaryDirty(
                    (contract_salary ?? null) !==
                      (e.target.value === ""
                        ? null
                        : Number.isNaN(Number(e.target.value))
                          ? null
                          : Number(e.target.value)),
                  )
                }}
              />
              {salaryDirty && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={async () => {
                      const newValue =
                        salaryValue === ""
                          ? null
                          : Number.isNaN(Number(salaryValue))
                            ? null
                            : Number(salaryValue)
                      try {
                        setIsSavingSalary(true)
                        const { error } = await supabase
                          .from("applications")
                          .update({ contract_salary: newValue })
                          .eq("id", id)
                        if (error) throw error
                        toast.success("Saved", {
                          description: "Contract salary updated",
                        })
                        setSalaryDirty(false)
                        onContractSalaryChange?.(newValue)
                      } catch (err) {
                        toast.error("Save failed", {
                          description:
                            err instanceof Error ? err.message : "Could not update contract salary",
                        })
                      } finally {
                        setIsSavingSalary(false)
                      }
                    }}
                    disabled={isSavingSalary}
                  >
                    {isSavingSalary ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isSavingSalary}
                    onClick={() => {
                      setSalaryValue(contract_salary != null ? String(contract_salary) : "")
                      setSalaryDirty(false)
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </RoleTeamGuard>

        <div>
          <p className="text-sm text-palette-jungleGreen500">Current Stage</p>
          {renderCurrentStage(stage)}
        </div>

        {segment ? (
          <div>
            <p className="text-sm text-palette-jungleGreen500">Current Segment</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="p-1.5 rounded-md border-2">
                <Workflow className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="font-medium">{getSegmentLabel(stage, segment)}</p>
            </div>
          </div>
        ) : null}

        {stage_reason && (
          <div>
            <p className="text-sm text-palette-jungleGreen500">Stage Reason</p>
            <p className="font-semibold text-primary">{renderStageReason(stage_reason)}</p>
          </div>
        )}

        {stage_note && (
          <div>
            <p className="text-sm text-palette-jungleGreen500">Stage Note</p>
            <p>{stage_note}</p>
          </div>
        )}

        <PermissionGuard action="update" scopes={["any", "own"]} resource="applications">
          <Button className="w-full mt-4" variant="default" onClick={onEditClick}>
            Edit Applicant Details
          </Button>

          <Button
            className="w-full mt-2"
            variant="outline"
            onClick={() => setIsMoveStageDialogOpen(true)}
          >
            Move Stage
          </Button>
        </PermissionGuard>
        <Separator className="my-4" />

        {!isDSP && <CommentsSection applicationId={id} />}
      </div>
      <MoveStageDialog
        isOpen={isMoveStageDialogOpen}
        onClose={() => setIsMoveStageDialogOpen(false)}
        currentStage={stage}
        currentSegment={segment}
        applicantName={applicantName}
        applicationId={id}
        personId={personId}
        allowedStages={allowedStages}
      />
    </div>
  )
}

export default StatusPanel
